import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Garantia, GarantiaConfiguracao, FiltroGarantia } from '@pgcustom/shared-types';

/**
 * Adapter do módulo Garantias no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/garantias/*`
 * (`apps/api/src/modules/garantias/garantias.routes.ts`). Mesmo padrão
 * de `EnviosApi`. Sem `criar`: garantia só nasce de listener de evento
 * (`envio.entregue`), nunca via HTTP.
 */
export class GarantiasApi {
  constructor(private readonly api: ApiClient) {}

  async listar(filtro: FiltroGarantia = {}): Promise<Resultado<Garantia[]>> {
    try {
      const resposta = await this.api.get<{ garantias: Garantia[] }>('/garantias', { ...filtro });
      if (resposta.garantias.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.garantias };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar garantias' };
    }
  }

  async obterConfiguracao(): Promise<GarantiaConfiguracao> {
    const resposta = await this.api.get<{ configuracao: GarantiaConfiguracao }>('/garantias/configuracao');
    return resposta.configuracao;
  }

  async definirConfiguracao(diasCobertura: number): Promise<GarantiaConfiguracao> {
    const resposta = await this.api.put<{ configuracao: GarantiaConfiguracao }>('/garantias/configuracao', { diasCobertura });
    return resposta.configuracao;
  }

  /** `POST /garantias/:id/reparo` — devolve a garantia e o id da OPS de reparo (`null` se o listener de `producao` não conseguiu criar, ver `garantias.service.ts`). */
  async solicitarReparo(id: string): Promise<{ garantia: Garantia; opsId: string | null }> {
    return this.api.post<{ garantia: Garantia; opsId: string | null }>(`/garantias/${id}/reparo`, {});
  }
}
